import { useQuery } from '@apollo/client/react'
import { useEffect, useMemo, useState } from 'react'
import { useAuthProvider } from 'src/context/AuthProvider'
import useLogger from 'src/context/LoggerProvider/useLogger'
import { INotificationCounts } from 'src/hooks/useNotificationCounts'
import { IQuery } from 'src/types/graphql'
import { RedirectUrls } from 'src/types/types'
import { deferToIdle } from 'src/utils/deferToIdle'
import { getDefaultDashboard } from 'src/utils/getDefaultDashboard'
import { AccountNav, resolveAccountNav } from './accountNav'
import { DASHBOARD_CONTEXT_QUERY } from './DashboardContext'
import { NAV_ITEMS, NavKey, ShellLabelKey } from './navItems'

export type AccountNavItem = {
  key: NavKey
  labelKey: ShellLabelKey
  href: string
  icon: string
  badge: number
}

export type AccountNavState = Omit<AccountNav, 'items'> & {
  items: AccountNavItem[]
}

/**
 * The public pages' view of the dashboard nav (avatar popover + mobile
 * drawer). Outside the dashboard there is no DashboardContextProvider, so this
 * reads `dashboardContext` itself — same operation and variables as the
 * /dashboard role landing, so the two share one cache entry. Logged-out
 * visitors never ask; logged-in ones ask once the page is idle (the menu is
 * closed on load). Admins skip it: their nav is platform-wide.
 *
 * `counts` comes from the caller's own useNotificationCounts — every instance
 * of that hook fires its queries (cache-and-network), so don't add another.
 */
export default function useAccountNav(
  counts: INotificationCounts
): AccountNavState {
  const { isAuthenticated, currentUser } = useAuthProvider()
  const logger = useLogger()
  const isAdmin = !!currentUser?.isAdmin
  const shouldAsk = isAuthenticated && !!currentUser && !isAdmin

  const [idle, setIdle] = useState(false)
  useEffect(() => {
    if (!shouldAsk) return
    return deferToIdle(() => setIdle(true))
  }, [shouldAsk])

  const { data, error } = useQuery<Pick<IQuery, 'dashboardContext'>>(
    DASHBOARD_CONTEXT_QUERY,
    { skip: !shouldAsk || !idle, fetchPolicy: 'cache-first' }
  )

  useEffect(() => {
    if (error) {
      logger.warn(
        'dashboardContext failed for the public account menu',
        { message: error.message },
        error
      )
    }
  }, [error, logger])

  const declaredHost =
    getDefaultDashboard(currentUser) === RedirectUrls.Dashboard
  const scopes = data?.dashboardContext.scopes ?? null

  return useMemo(() => {
    const nav = resolveAccountNav({
      scopes,
      loading: !scopes && !error,
      failed: !!error,
      isAdmin,
      declaredHost
    })
    return {
      ...nav,
      items: nav.items.map((key) => ({
        key,
        labelKey: NAV_ITEMS[key].labelKey,
        // Public pages carry no scope pill → the "Todas" view.
        href: NAV_ITEMS[key].href({
          role: nav.role,
          selectedTeamId: 'all',
          selectedHostId: null
        }),
        icon: NAV_ITEMS[key].icon(nav.role),
        badge: NAV_ITEMS[key].badge(counts, nav.role)
      }))
    }
  }, [scopes, error, isAdmin, declaredHost, counts])
}
